/**
 * HumanoidAvatar — humanoid avatar with floating brand/club patches.
 * Displays the user's DiceBear humanoid (style "personas" / "open-peeps")
 * surrounded by 5 corner patches:
 *   • Top-left:     Pala (paddle brand)
 *   • Top-right:    Ropa (apparel brand)
 *   • Bottom-left:  Calzado (shoes brand)
 *   • Bottom-right: Accesorios (free text or sponsor brand)
 *   • Center-bottom (overlapping): Club shield (if user has club)
 *
 * Each patch shows a logo (when available) or a 3-letter chip with brand colors.
 */
import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { getAvatarUrl, getInitials, getPalaBrand, getApparelBrand, getShoesBrand } from "@/lib/avatar";

const API_URL = process.env.REACT_APP_BACKEND_URL;
const absLogo = (u) => {
  if (!u) return "";
  if (u.startsWith("http://") || u.startsWith("https://")) return u;
  if (u.startsWith("/")) return `${API_URL}${u}`;
  return u;
};

function BrandPatch({ brand, label, position, size = 32, testId }) {
  if (!brand) return null;
  const positions = {
    "top-left":     "top-1 left-1",
    "top-right":    "top-1 right-1",
    "bottom-left":  "bottom-1 left-1",
    "bottom-right": "bottom-1 right-1",
  };
  return (
    <div
      className={`absolute ${positions[position]} flex items-center justify-center border-2 overflow-hidden shadow-lg`}
      style={{
        width: size, height: size,
        backgroundColor: brand.color || brand.primary || "#1A1A1A",
        color: brand.text || brand.accent || "#FFF",
        borderColor: "#0A0A0A",
        borderRadius: 6,
      }}
      title={`${label}: ${brand.name}`}
      data-testid={testId}
    >
      {brand.logo ? (
        <img
          src={absLogo(brand.logo)}
          alt={brand.name}
          className="w-full h-full object-contain p-0.5"
          style={{ backgroundColor: brand.text === "#FFFFFF" ? brand.color : "#FFFFFF" }}
          onError={(e) => { e.currentTarget.style.display = "none"; }}
        />
      ) : (
        <span className="text-[9px] font-black uppercase tracking-tight leading-none px-0.5 text-center">
          {(brand.short || brand.name?.slice(0, 3) || "").toUpperCase()}
        </span>
      )}
    </div>
  );
}

export default function HumanoidAvatar({ user, size = 144, showLabels = false, editLink = "/dashboard/mi-perfil", testId = "humanoid-avatar", arGlow = false }) {
  const [club, setClub] = useState(null);

  // Force humanoid style (personas / open-peeps)
  const humanoidUser = user ? { ...user, avatar_style: ["personas", "open-peeps", "notionists"].includes(user?.avatar_style) ? user.avatar_style : "personas" } : null;
  const url = humanoidUser ? getAvatarUrl({ ...humanoidUser, photo_path: null }, size * 2) : null;

  const pala = getPalaBrand(user?.gear_pala_brand);
  const apparel = getApparelBrand(user?.gear_apparel_brand);
  const shoes = getShoesBrand(user?.gear_shoes_brand);
  const accessories = user?.gear_accessories
    ? { name: user.gear_accessories, color: "#1A1A1A", text: "#CCFF00", short: user.gear_accessories.slice(0, 3) }
    : null;

  useEffect(() => {
    if (!user?.club_id) { setClub(null); return; }
    api.get(`/clubs/${user.club_id}`)
      .then(({ data }) => setClub(data))
      .catch(() => setClub(null));
  }, [user?.club_id]);

  const patchSize = Math.max(36, Math.round(size * 0.28));

  if (!user) return null;

  const inner = url ? (
    <img
      src={url}
      alt={user.name || "Humanoid avatar"}
      width={size}
      height={size}
      className="w-full h-full object-cover bg-[#0A0A0A]"
    />
  ) : (
    <div className="w-full h-full flex items-center justify-center bg-[#1A1A1A] text-white font-black text-2xl">
      {getInitials(user.name)}
    </div>
  );

  return (
    <div className="flex flex-col items-center gap-2" data-testid={testId}>
      <div
        className="relative border-2 border-[#CCFF00]/40 bg-[#0A0A0A]"
        style={{ width: size, height: size, borderRadius: 12 }}
      >
        {/* AR neon glow halo + lightning */}
        {arGlow && (
          <>
            <span
              aria-hidden
              className="absolute -inset-4 rounded-full blur-2xl pointer-events-none animate-pulse"
              style={{ background: "radial-gradient(circle, rgba(0,160,255,0.4) 0%, transparent 70%)" }}
            />
            <svg className="absolute -inset-4 w-[calc(100%+32px)] h-[calc(100%+32px)] pointer-events-none" viewBox="0 0 200 200" fill="none" aria-hidden>
              <path d="M100 0 L80 60 L120 60 L100 130" stroke="#00A0FF" strokeWidth="1.5" opacity="0.7" />
              <path d="M0 80 L60 100 L120 80 L200 110" stroke="#CCFF00" strokeWidth="1" opacity="0.6" strokeDasharray="4 3" />
            </svg>
          </>
        )}

        {inner}

        {/* Brand patches in 4 corners */}
        <BrandPatch brand={pala} label="Pala" position="top-left" size={patchSize} testId="humanoid-patch-pala" />
        <BrandPatch brand={apparel} label="Ropa" position="top-right" size={patchSize} testId="humanoid-patch-apparel" />
        <BrandPatch brand={shoes} label="Calzado" position="bottom-left" size={patchSize} testId="humanoid-patch-shoes" />
        <BrandPatch brand={accessories} label="Accesorios" position="bottom-right" size={patchSize} testId="humanoid-patch-accessories" />

        {/* Club shield overlapping bottom center */}
        {club && (
          <div
            className="absolute -bottom-3 left-1/2 -translate-x-1/2 px-2 py-1 bg-[#CCFF00] text-black text-[9px] font-black uppercase tracking-widest border-2 border-[#0A0A0A] shadow-lg whitespace-nowrap max-w-[90%] truncate"
            title={`Club: ${club.name}`}
            data-testid="humanoid-patch-club"
          >
            🏆 {club.name}
          </div>
        )}
      </div>

      {showLabels && (
        <Link
          to={editLink}
          className="text-[9px] uppercase tracking-[0.2em] text-[#CCFF00] hover:underline font-semibold mt-2"
          data-testid="humanoid-edit-link"
        >
          ✨ Editar Avatar y Equipamiento
        </Link>
      )}

      {showLabels && (
        <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-[9px] uppercase tracking-wider mt-1">
          {pala && <p className="text-gray-400">Pala: <span className="text-white">{pala.name}</span></p>}
          {apparel && <p className="text-gray-400">Ropa: <span className="text-white">{apparel.name}</span></p>}
          {shoes && <p className="text-gray-400">Calzado: <span className="text-white">{shoes.name}</span></p>}
          {accessories && <p className="text-gray-400">Acc: <span className="text-white">{accessories.name}</span></p>}
        </div>
      )}
    </div>
  );
}
